import { type FC, type ReactNode, useMemo } from 'react'
import { Chip } from '@/components/commons/chip'
import { Skeleton } from '@/components/ui/skeleton'
import { EditingQaChip } from '@/features/photoset/components/editing-qa-chip'
import { useAddImagesFlag } from '@/features/photoset/queries/use-add-images-flag'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import { useRemoveImagesFlag } from '@/features/photoset/queries/use-remove-images-flag'
import {
	type Flag,
	type Image,
	type PhotosetSpace,
	StepKey
} from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

const GalleryFlagsContent = ({
	isLoading,
	isError,
	remappedFlags,
	stepId,
	photosetSpaceIds,
	image,
	isQaView,
	imageFlagsIds,
	handleUpdateFlagToImages
}: {
	isLoading: boolean
	isError: boolean
	remappedFlags: Flag[]
	stepId: StepKey
	photosetSpaceIds?: PhotosetSpace['id'][]
	image: Omit<Image, 'space'>
	isQaView: boolean
	imageFlagsIds: number[]
	handleUpdateFlagToImages: (flag: Flag) => void
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return Array.from({ length: 4 }, (_, i) => `skeleton-${i}`).map(
			(key) => <Skeleton height={16} key={key} width={35} />
		)
	}
	if (isError) {
		return <p>{t('errors:default')}</p>
	}
	if (remappedFlags.length > 0) {
		return remappedFlags.map((flag) =>
			stepId === StepKey['editing-qa-flags'] ? (
				photosetSpaceIds && (
					<EditingQaChip
						flag={flag}
						image={image}
						isQaView={isQaView}
						key={flag.id}
						label={t(`photoset:flags.${flag.key_name}.label`)}
						photosetSpaceIds={photosetSpaceIds}
					/>
				)
			) : (
				<Chip
					isActive={imageFlagsIds.includes(flag.id)}
					key={flag.id}
					label={t(`photoset:flags.${flag.key_name}.label`)}
					onClick={() => handleUpdateFlagToImages(flag)}
					variant={
						imageFlagsIds.includes(flag.id) ? 'info' : 'neutral'
					}
				/>
			)
		)
	}
	return <p>{t('common:no_results')}</p>
}

interface GalleryFlagsProps {
	image: Omit<Image, 'space'>
	stepId: StepKey
	types?: Flag['type'][]
	photosetSpaceIds?: PhotosetSpace['id'][]
	endSlot?: ReactNode
	isQaView?: boolean
}

export const GalleryFlags: FC<GalleryFlagsProps> = ({
	image,
	stepId,
	types = ['metadata', 'visual_metadata', 'visual'],
	photosetSpaceIds,
	endSlot,
	isQaView = false
}) => {
	const { t } = useTranslation()

	const {
		data: flags,
		isLoading,
		isError
	} = useGetFlags({
		params: {
			types,
			response_type: 'extended'
		}
	})

	const addImagesFlag = useAddImagesFlag({ stepId, photosetSpaceIds })
	const removeImagesFlag = useRemoveImagesFlag({ stepId, photosetSpaceIds })

	const imageFlagsIds = useMemo(
		() => image.flags.map((flag) => flag.id),
		[image]
	)
	const remappedFlags = useMemo(
		() =>
			// Check the stepId to identify the editing qa flags step
			stepId !== StepKey['editing-qa-flags']
				? (flags ?? [])
				: (image.flags ?? []),
		[stepId, flags, image.flags]
	)

	const handleUpdateFlagToImages = (flag: Flag) => {
		const mutationFunction = imageFlagsIds.includes(flag.id)
			? removeImagesFlag
			: addImagesFlag
		mutationFunction.mutate({ flag, imageIds: [image.id] })
	}

	return (
		<div className='flex w-full flex-col [&_h3]:px-6 [&_h3]:py-5 [&_h3]:font-medium [&_h3]:text-lg [&_h3]:text-neutral-900'>
			<div className='flex w-full items-center justify-between'>
				<h3>{t('common:flag', { count: 2 })}</h3>

				{endSlot}
			</div>

			<div className='flex w-full flex-wrap gap-2.5 px-6 pb-[30px]'>
				<GalleryFlagsContent
					handleUpdateFlagToImages={handleUpdateFlagToImages}
					image={image}
					imageFlagsIds={imageFlagsIds}
					isError={isError}
					isLoading={isLoading}
					isQaView={isQaView}
					photosetSpaceIds={photosetSpaceIds}
					remappedFlags={remappedFlags}
					stepId={stepId}
				/>
			</div>
		</div>
	)
}
